import { useMemo } from 'react';

import type {
  EnhancePanelAction,
  EnhanceSubcategory,
} from '@modules/_shared/components/image-gallery/enhance/enhance-image-types';
import { useLocale } from '@shared/hooks/locale';

/** Anchor every property prompt: preserve all scene content. */
const PRESERVE =
  'Preserve all architecture, furniture, objects, views through windows, landscaping, trees, and room contents exactly as in the reference image. ';

/** Regional / sky / lawn edits: anchor Google's "change only this region" pattern. */
const REGIONAL_ONLY =
  'Change only the described region; keep all other architecture, landscaping, trees, and ground unchanged from the reference. ';

export const useEnhanceImageActions = () => {
  const { t } = useLocale();

  return useMemo(() => {
    const categories = [
      { id: 'all', label: t('All'), emoji: '✦' },
      { id: 'quality', label: t('Quality'), emoji: '✨' },
      { id: 'lighting', label: t('Lighting'), emoji: '☀️' },
      { id: 'staging', label: t('Staging'), emoji: '🛋️' },
      { id: 'cleanup', label: t('Cleanup'), emoji: '🧹' },
      { id: 'exterior', label: t('Exterior'), emoji: '🏡' },
    ];

    const subcategories: EnhanceSubcategory[] = [
      {
        id: 'quality-detail',
        categoryId: 'quality',
        emoji: '🔍',
        label: t('Sharpness & resolution'),
      },
      {
        id: 'quality-color',
        categoryId: 'quality',
        emoji: '🎨',
        label: t('Color & HDR look'),
      },
      {
        id: 'quality-listing',
        categoryId: 'quality',
        emoji: '🖼️',
        label: t('MLS & web ready'),
      },
      {
        id: 'quality-windows',
        categoryId: 'quality',
        emoji: '🪟',
        label: t('Windows & glass'),
      },
      {
        id: 'lighting-daylight',
        categoryId: 'lighting',
        emoji: '☀️',
        label: t('Bright & balanced'),
      },
      {
        id: 'lighting-mood',
        categoryId: 'lighting',
        emoji: '🌆',
        label: t('Mood & atmosphere'),
      },
      {
        id: 'lighting-luxury',
        categoryId: 'lighting',
        emoji: '💎',
        label: t('Luxury & drama'),
      },
      {
        id: 'staging-virtual',
        categoryId: 'staging',
        emoji: '🛋️',
        label: t('Virtual staging'),
      },
      {
        id: 'staging-designer',
        categoryId: 'staging',
        emoji: '✨',
        label: t('Designer styles'),
      },
      {
        id: 'staging-traditional',
        categoryId: 'staging',
        emoji: '🏛️',
        label: t('Classic & traditional'),
      },
      {
        id: 'cleanup-space',
        categoryId: 'cleanup',
        emoji: '🧹',
        label: t('Space & clutter'),
      },
      {
        id: 'cleanup-photo',
        categoryId: 'cleanup',
        emoji: '📷',
        label: t('Photo corrections'),
      },
      {
        id: 'cleanup-surfaces',
        categoryId: 'cleanup',
        emoji: '✨',
        label: t('Floors & surfaces'),
      },
      {
        id: 'exterior-sky-ground',
        categoryId: 'exterior',
        emoji: '🌤️',
        label: t('Sky & grounds'),
      },
      {
        id: 'exterior-twilight',
        categoryId: 'exterior',
        emoji: '🌇',
        label: t('Twilight & curb appeal'),
      },
      {
        id: 'exterior-facade',
        categoryId: 'exterior',
        emoji: '🏠',
        label: t('Facade & driveway'),
      },
    ];

    const actions: EnhancePanelAction[] = [
      {
        id: 'listing-quality-studio',
        label: t('Studio quality'),
        emoji: '✨',
        description: t('Professional studio-grade enhancement'),
        category: 'quality',
        subcategory: 'quality-detail',
        prompt:
          PRESERVE +
          'Enhance this uploaded image to the absolute highest professional studio quality possible. Intelligently upscale to crisp native 4K resolution using advanced super-resolution techniques: generate realistic missing pixels and ultra-fine details everywhere (skin, fabrics, hair, textures, surfaces, lighting) while staying 100% faithful to the original content, colors, and composition — never invent new objects or change anything, only enhance and fill in what is already implied. Dramatically increase sharpness with perfect edge definition and depth, boost natural vibrant colors with rich contrast and full HDR dynamic range, remove all noise, blur, compression artifacts and imperfections, add photorealistic micro-textures and depth, ensure flawless lighting, shadows and highlights — masterpiece, best quality, ultra-detailed, 4K native super-resolution, no artifacts, perfect composition.',
      },
      {
        id: 'listing-quality-upscale',
        label: t('Upscale & sharpen'),
        emoji: '🔍',
        description: t('Increase resolution and clarity'),
        category: 'quality',
        subcategory: 'quality-detail',
        prompt:
          PRESERVE +
          'Upscale this uploaded real estate image to maximum resolution with razor-sharp detail. Intelligently generate missing pixels using super-resolution, enhance every edge and texture for crystal-clear definition, remove any blur or softness while preserving 100% of the original content, colors and composition — crisp 4K quality, no artifacts, photorealistic sharpening, professional real estate photography standard.',
      },
      {
        id: 'listing-quality-contrast',
        label: t('Contrast & color'),
        emoji: '🎨',
        description: t('Boost colors and dynamic range'),
        category: 'quality',
        subcategory: 'quality-color',
        prompt:
          PRESERVE +
          'Enhance this uploaded image with extreme professional contrast boost and stunning color improvement. Dramatically increase contrast for deep rich blacks, brilliant luminous highlights, crisp mid-tones and full HDR dynamic range with powerful depth and pop, significantly boost and perfect the colors to be ultra-vibrant, richly saturated, natural and lifelike with accurate professional color grading, intelligent saturation enhancement without oversaturation or artifacts, improve color accuracy and tonal separation, remove any dullness or flatness, make every hue vivid and harmonious while keeping skin tones perfectly natural and flattering, add incredible visual impact and cinematic polish — ultra high contrast, vibrant enhanced colors, masterpiece, best quality, photorealistic, professional studio color grading.',
      },
      {
        id: 'listing-quality-hdr',
        label: t('HDR effect'),
        emoji: '📸',
        description: t('High dynamic range look'),
        category: 'quality',
        subcategory: 'quality-color',
        prompt:
          PRESERVE +
          'Apply professional HDR (High Dynamic Range) enhancement to this uploaded real estate photo. Recover all detail in bright windows and dark corners simultaneously, create perfectly balanced exposure throughout the entire image, make shadows luminous without looking washed out, keep highlights brilliant without blowout, add rich tonal depth and dimension — professional real estate HDR photography, balanced exposure, vivid yet natural, magazine-quality listing photo.',
      },
      {
        id: 'listing-lighting-natural',
        label: t('Natural light'),
        emoji: '☀️',
        description: t('Bright, even window light'),
        category: 'lighting',
        subcategory: 'lighting-daylight',
        prompt:
          PRESERVE +
          'Enhance this uploaded real estate photo with perfect natural window light and balanced illumination. Pull in soft, bright daylight through all windows, eliminate harsh shadows or dark corners, create even lighting throughout the space with gentle glow and natural reflections, make the room feel sunny and welcoming while keeping the exact same time of day and realism — bright airy natural lighting, professional real estate photography style.',
      },
      {
        id: 'listing-lighting-fix-dark',
        label: t('Fix dark areas'),
        emoji: '💡',
        description: t('Brighten shadows and corners'),
        category: 'lighting',
        subcategory: 'lighting-daylight',
        prompt:
          PRESERVE +
          'Enhance this uploaded real estate photo by intelligently brightening all dark areas, shadows and underexposed corners. Lift shadow detail to reveal hidden textures and features, balance the exposure so every part of the room is clearly visible, maintain natural lighting direction and color temperature, do not overexpose highlights — clean, even, professional real estate lighting with no dark spots or murky areas.',
      },
      {
        id: 'listing-lighting-twilight',
        label: t('Twilight mood'),
        emoji: '🌆',
        description: t('Evening blue hour atmosphere'),
        category: 'lighting',
        subcategory: 'lighting-mood',
        prompt:
          PRESERVE +
          'Convert this uploaded real estate image to beautiful twilight or dusk atmosphere. Change the lighting to magical evening blue hour with warm glowing interior lights visible through windows, soft landscape and pathway illumination, deep colorful sky, create a cozy yet luxurious evening mood while preserving all architecture and details exactly — dramatic twilight real estate photography, inviting and high-end listing style.',
      },
      {
        id: 'listing-lighting-warm',
        label: t('Warm & cozy'),
        emoji: '🕯️',
        description: t('Soft warm interior ambiance'),
        category: 'lighting',
        subcategory: 'lighting-mood',
        prompt:
          PRESERVE +
          'Enhance this uploaded real estate interior photo with warm, inviting ambient lighting. Add soft warm-toned illumination that makes the space feel cozy and welcoming, gentle golden glow from lamps and fixtures, warm color temperature throughout, subtle highlights on wood and fabric textures, create an intimate livable atmosphere while keeping all architecture and furniture unchanged — warm inviting real estate photography, homey and luxurious feel.',
      },
      {
        id: 'listing-staging-virtual',
        label: t('Virtual staging'),
        emoji: '🛋️',
        description: t('Add modern furniture and decor'),
        category: 'staging',
        subcategory: 'staging-virtual',
        prompt:
          PRESERVE +
          'Apply intelligent virtual staging to this uploaded real estate image. Add beautiful, modern, neutral furniture and tasteful decor that perfectly matches the room style and scale (sofa, coffee table, lamps, art, plants etc.), keep all architecture, windows, flooring and lighting 100% unchanged and realistic, make the space look fully furnished and move-in ready — high-end professionally staged look, photorealistic, luxury listing quality.',
      },
      {
        id: 'listing-staging-modern',
        label: t('Modern style'),
        emoji: '🏢',
        description: t('Contemporary minimalist staging'),
        category: 'staging',
        subcategory: 'staging-designer',
        prompt:
          PRESERVE +
          'Apply contemporary modern virtual staging to this uploaded real estate image. Add sleek minimalist furniture with clean lines, neutral tones (white, grey, black, natural wood), statement lighting fixtures, abstract art, and carefully curated accessories. Keep architecture, windows, flooring 100% unchanged — high-end modern interior design, magazine-worthy, photorealistic staging.',
      },
      {
        id: 'listing-staging-scandi',
        label: t('Scandinavian'),
        emoji: '🪵',
        description: t('Light wood, clean lines'),
        category: 'staging',
        subcategory: 'staging-designer',
        prompt:
          PRESERVE +
          'Apply Scandinavian-style virtual staging to this uploaded real estate image. Add light oak and birch wood furniture, soft white and cream textiles, cozy throws and cushions, simple ceramic vases, greenery, warm minimalist lighting. Create a hygge atmosphere with clean functional design. Keep architecture, windows, flooring 100% unchanged — bright, airy Nordic interior, photorealistic staging.',
      },
      {
        id: 'listing-staging-luxury',
        label: t('Luxury style'),
        emoji: '👑',
        description: t('High-end premium furnishings'),
        category: 'staging',
        subcategory: 'staging-designer',
        prompt:
          PRESERVE +
          'Apply luxury high-end virtual staging to this uploaded real estate image. Add premium designer furniture, rich materials (marble, velvet, brass, leather), statement chandelier or designer lighting, curated art pieces, elegant accessories and fresh flowers. Create an opulent yet tasteful atmosphere. Keep architecture, windows, flooring 100% unchanged — five-star luxury interior design, photorealistic staging.',
      },
      {
        id: 'listing-cleanup-declutter',
        label: t('Declutter & clean'),
        emoji: '🧹',
        description: t('Remove personal items and mess'),
        category: 'cleanup',
        subcategory: 'cleanup-space',
        prompt:
          PRESERVE +
          'Enhance this uploaded real estate image with intelligent virtual decluttering and deep cleaning. Remove all personal items, small objects, toys, papers, cords, dust, and distractions from counters, floors, tables and shelves while keeping all major furniture and architecture 100% intact and realistic, make every surface spotless and magazine-ready, seamless object removal with no traces or artifacts — perfectly staged, clean, move-in ready look for luxury listings.',
      },
      {
        id: 'listing-cleanup-objects',
        label: t('Remove objects'),
        emoji: '🗑️',
        description: t('Clean up unwanted items'),
        category: 'cleanup',
        subcategory: 'cleanup-space',
        prompt:
          PRESERVE +
          'Intelligently remove all unwanted and distracting objects from this uploaded real estate photo. Eliminate clutter, personal belongings, trash bins, cords, cleaning supplies, and any items that detract from the space. Fill in removed areas with realistic textures matching the surrounding surfaces — seamless, clean, professional real estate photography with no traces of removal.',
      },
      {
        id: 'listing-cleanup-glare',
        label: t('Remove glare'),
        emoji: '🪟',
        description: t('Fix window reflections'),
        category: 'cleanup',
        subcategory: 'cleanup-photo',
        prompt:
          PRESERVE +
          'Enhance this uploaded real estate interior photo by intelligently removing all window glare and reflections. Clear every glass surface so you can see the beautiful view outside clearly, eliminate harsh reflections, light streaks and hotspots on windows, mirrors and shiny surfaces, keep the exact same lighting and room details — crystal-clear windows, bright and inviting real estate photo, professional quality.',
      },
      {
        id: 'listing-cleanup-perspective',
        label: t('Fix perspective'),
        emoji: '📐',
        description: t('Correct lens distortion'),
        category: 'cleanup',
        subcategory: 'cleanup-photo',
        prompt:
          PRESERVE +
          'Correct the perspective and lens distortion in this uploaded real estate photo. Straighten all vertical and horizontal lines, fix barrel or pincushion distortion, ensure walls are perfectly vertical and floors/ceilings are level, correct any wide-angle warping while maintaining natural proportions — clean professional architectural photography with perfect geometry.',
      },
      {
        id: 'listing-exterior-sky',
        label: t('Blue sky'),
        emoji: '🌤️',
        description: t('Replace overcast sky with blue'),
        category: 'exterior',
        subcategory: 'exterior-sky-ground',
        prompt:
          REGIONAL_ONLY +
          'Replace the sky in this uploaded real estate exterior photo with a beautiful clear blue sky with a few wispy white clouds. Match the lighting direction and color temperature to the new sky, adjust reflections on windows and surfaces accordingly, keep the entire building and landscaping 100% unchanged — bright inviting exterior real estate photography with perfect blue sky.',
      },
      {
        id: 'listing-exterior-lawn',
        label: t('Green lawn'),
        emoji: '🌿',
        description: t('Lush green landscaping'),
        category: 'exterior',
        subcategory: 'exterior-sky-ground',
        prompt:
          REGIONAL_ONLY +
          'Enhance the landscaping in this uploaded real estate exterior photo. Make all grass and lawns lush, thick, and vibrant green, enhance trees and shrubs to look healthy and full, brighten flower beds, clean up any bare patches or brown spots. Keep all hardscaping, buildings and structures 100% unchanged — beautiful manicured garden and landscaping, professional real estate photography.',
      },
      {
        id: 'listing-exterior-dusk',
        label: t('Day to dusk'),
        emoji: '🌇',
        description: t('Twilight exterior conversion'),
        category: 'exterior',
        subcategory: 'exterior-twilight',
        prompt:
          REGIONAL_ONLY +
          'Convert this uploaded real estate exterior photo to a stunning twilight/dusk scene. Transform the sky to a dramatic blue-purple-orange sunset gradient, add warm golden light glowing from all interior windows, illuminate the landscape with soft pathway and accent lighting, create a magical evening atmosphere while keeping the entire building and property unchanged — luxury twilight real estate photography, high-end listing quality.',
      },
      {
        id: 'listing-exterior-curb',
        label: t('Curb appeal'),
        emoji: '🏡',
        description: t('Overall exterior enhancement'),
        category: 'exterior',
        subcategory: 'exterior-twilight',
        prompt:
          REGIONAL_ONLY +
          'Maximize the curb appeal of this uploaded real estate exterior photo. Enhance the sky to clear blue, make landscaping lush and green, brighten and clean all exterior surfaces, improve driveway and walkway appearance, add warm inviting glow from windows, boost overall color vibrancy and contrast while keeping the property 100% architecturally unchanged — stunning first-impression exterior photography, professional listing quality.',
      },
      {
        id: 'listing-quality-mls',
        label: t('MLS-ready polish'),
        emoji: '🖼️',
        description: t('Balanced look for portals and brochures'),
        category: 'quality',
        subcategory: 'quality-listing',
        prompt:
          PRESERVE +
          'Optimize this real estate listing photo for MLS, portals, and print brochures. Preserve exact room layout, architecture, and colors. Apply clean, neutral professional color correction with natural white balance, gentle contrast, and pleasing mid-tones so the image reads clearly at thumbnail size and full screen. Avoid oversaturation and heavy HDR halos — trustworthy, accurate, brokerage-quality presentation.',
      },
      {
        id: 'listing-quality-denoise',
        label: t('Clean & noise-free'),
        emoji: '🧼',
        description: t('Reduce grain from low light or high ISO'),
        category: 'quality',
        subcategory: 'quality-listing',
        prompt:
          PRESERVE +
          'Clean up this real estate interior or exterior photo for professional listing use. Preserve architecture, furniture, and true colors. Remove digital noise, color speckle, and compression grain while recovering fine texture in walls, wood, and fabrics — smooth yet natural, not plastic. Keep edges sharp on architectural lines. Ideal for dim interiors or twilight shots shot at high ISO.',
      },
      {
        id: 'listing-quality-windows-balance',
        label: t('Balanced windows'),
        emoji: '🪟',
        description: t('Recover detail inside and outside the glass'),
        category: 'quality',
        subcategory: 'quality-windows',
        prompt:
          PRESERVE +
          'Enhance this real estate interior photo for balanced window exposure. Preserve the room and view exactly as composed. Recover detail in bright window areas and outdoor views without blowing out interiors; lift shadowed room zones slightly so the space feels evenly lit. Keep window frames and mullions sharp. Natural, believable HDR-style balance suitable for luxury listings — no fake sky replacement unless the view already exists.',
      },
      {
        id: 'listing-quality-glass',
        label: t('Clear glass & views'),
        emoji: '🔆',
        description: t('Reduce harsh reflections on glass'),
        category: 'quality',
        subcategory: 'quality-windows',
        prompt:
          PRESERVE +
          'Improve clarity of windows, glass doors, and reflective surfaces in this real estate photo. Preserve architecture and outdoor views. Subtly reduce harsh reflections and hotspots on glass while keeping natural transparency so gardens, city, or landscape remain visible. Do not remove or invent views — only enhance legibility and professional polish for listing photography.',
      },
      {
        id: 'listing-lighting-luxury-glow',
        label: t('Luxury interior glow'),
        emoji: '💎',
        description: t('High-end residential lighting'),
        category: 'lighting',
        subcategory: 'lighting-luxury',
        prompt:
          PRESERVE +
          'Enhance this real estate interior with refined luxury-residential lighting. Preserve layout and furnishings. Add soft layered light: gentle key from windows, subtle fill, delicate highlights on premium materials (marble, brass, hardwood), and controlled shadows for depth. Warm-neutral color temperature, elegant and expensive feel — editorial luxury listing style, not theatrical or oversaturated.',
      },
      {
        id: 'listing-lighting-editorial',
        label: t('Editorial drama'),
        emoji: '📰',
        description: t('Bold contrast for hero shots'),
        category: 'lighting',
        subcategory: 'lighting-luxury',
        prompt:
          PRESERVE +
          'Apply bold editorial real estate lighting to this photo for a hero marketing image. Preserve architecture and composition. Increase controlled contrast with deep but detailed shadows, crisp highlights, and cinematic mood while keeping colors believable. Strong architectural presence — magazine cover or premium developer brochure style, still photorealistic.',
      },
      {
        id: 'listing-staging-classic',
        label: t('Classic traditional staging'),
        emoji: '🏛️',
        description: t('Timeless furniture and decor'),
        category: 'staging',
        subcategory: 'staging-traditional',
        prompt:
          PRESERVE +
          'Apply classic traditional virtual staging to this empty or sparse real estate room. Add timeless furniture: rolled-arm sofa, wood coffee table, refined lamps, tasteful drapery, and warm neutral palette. Symmetrical, elegant arrangement. Keep walls, windows, floors, and architecture 100% unchanged — upscale suburban or heritage home listing quality, photorealistic.',
      },
      {
        id: 'listing-staging-transitional',
        label: t('Transitional staging'),
        emoji: '🔑',
        description: t('Blend of classic comfort and modern clean lines'),
        category: 'staging',
        subcategory: 'staging-traditional',
        prompt:
          PRESERVE +
          'Apply transitional virtual staging to this real estate image. Blend classic comfort with modern clean lines: streamlined sofa, mixed wood and metal accents, soft textiles, restrained decor. Neutral warm palette. Keep all architecture, windows, and flooring exactly as photographed — broad buyer appeal, move-in ready luxury listing look, photorealistic.',
      },
      {
        id: 'listing-cleanup-floors',
        label: t('Floors & wood shine'),
        emoji: '🪵',
        description: t('Refresh hardwood and tile'),
        category: 'cleanup',
        subcategory: 'cleanup-surfaces',
        prompt:
          PRESERVE +
          'Enhance floors and hard surfaces in this real estate photo for listing quality. Preserve room geometry and lighting. Deepen richness of hardwood grain, reduce scuffs and dullness, brighten tile and stone with natural specular highlights. Keep reflections believable. Do not change floor pattern or materials — only polish and clarity for professional property marketing.',
      },
      {
        id: 'listing-cleanup-kitchen',
        label: t('Kitchen surfaces'),
        emoji: '🍽️',
        description: t('Counters and fixtures that pop'),
        category: 'cleanup',
        subcategory: 'cleanup-surfaces',
        prompt:
          PRESERVE +
          'Polish kitchen surfaces in this real estate listing photo. Preserve layout, cabinets, and appliances. Enhance countertops (stone, quartz, laminate) with subtle clarity and clean highlights; brighten sink and faucet metal; reduce water spots and smudges on reflective surfaces. Natural, hygienic, magazine-ready kitchen — no object removal beyond minor spot cleanup.',
      },
      {
        id: 'listing-exterior-facade',
        label: t('Facade & siding'),
        emoji: '🧱',
        description: t('Clean building front and materials'),
        category: 'exterior',
        subcategory: 'exterior-facade',
        prompt:
          REGIONAL_ONLY +
          'Refresh the facade and exterior materials in this property photo. Preserve exact architecture and proportions. Brighten and clean siding, stucco, brick, or wood cladding; reduce stains and uneven weathering while keeping texture natural; sharpen edges for a well-maintained first impression. Do not change windows, roofline, or structure — professional curb appeal for listings.',
      },
      {
        id: 'listing-exterior-driveway',
        label: t('Driveway & entrance'),
        emoji: '🚗',
        description: t('Approach and path look inviting'),
        category: 'exterior',
        subcategory: 'exterior-facade',
        prompt:
          REGIONAL_ONLY +
          'Improve driveway, walkway, and entrance approach in this real estate exterior photo. Preserve hardscape layout and landscaping. Reduce stains and patchy discoloration on concrete, pavers, or gravel; enhance edge definition; keep lines straight and clean. Welcoming, well-maintained arrival experience for buyers — photorealistic, no new structures.',
      },
    ];

    return { categories, subcategories, actions };
  }, [t]);
};
